<script setup lang="ts">
import type { Media } from "@/models"
import { onClickOutside } from "@vueuse/core"
import { useTemplateRef } from "vue"
import { BaseModal, Button } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { MediaType } from "@/models"
import { MediaViewModalKey } from "@/symbols"

interface MediaViewModalProps {
  media?: Media
  isTaggingEnabled?: boolean
}

defineProps<MediaViewModalProps>()

const emit = defineEmits<{
  (e: "close"): void
}>()

const { t, d } = useTranslation()
const { toggle, isActive } = useModal(MediaViewModalKey)

const modalContentRef = useTemplateRef<HTMLElement>("modalContentRef")

function onClose() {
  emit("close")
  toggle()
}

onClickOutside(
  modalContentRef,
  () => {
    if (!isActive?.value)
      return
    onClose()
  },
  { ignore: [".gpx-toast-container"] },
)
</script>

<template>
  <BaseModal :name="MediaViewModalKey" data-test="media-view-modal">
    <div ref="modalContentRef" class="bg-white rounded-lg overflow-hidden max-h-[80vh] flex flex-col">
      <div class="flex-shrink-0 bg-white p-4 pr-0 border-b border-gray-200">
        <div class="flex items-center justify-between md:gap-20 gap-3">
          <div class="min-w-0 flex-1">
            <h1 class="font-comfortaa font-semibold text-xl truncate" :title="media?.title || media?.fileName">
              {{ media?.title || media?.fileName }}
            </h1>
          </div>
          <Button
            variant="flat"
            prefix-icon="Close"
            class="p-0"
            data-test="media-view-close-button"
            @click="onClose"
          />
        </div>
      </div>

      <div class="flex-1 overflow-y-auto font-montserrat">
        <div v-if="media">
          <div class="flex items-center justify-center bg-black/5 p-4">
            <div class="relative flex items-center justify-center">
              <div v-if="media?.type === MediaType.Vid">
                <video
                  playsinline
                  :poster="media?.getThumbnailUrl()"
                  controls
                  class="max-w-full max-h-[50vh] w-auto h-auto object-contain"
                  controlsList="nodownload nofullscreen"
                  disablePictureInPicture
                  @contextmenu.prevent
                >
                  <source :src="media?.thumbnailUrl" type="video/mp4">
                </video>
              </div>
              <img
                v-else
                :src="media.fullSizeUrl || media.getThumbnailUrl()"
                :alt="media.title || media.fileName"
                class="max-w-full max-h-[50vh] w-auto h-auto object-contain"
                @contextmenu.prevent
              >
            </div>
          </div>
          <div class="space-y-4 p-6">
            <div v-if="media.title">
              <h3 class="text-lg font-semibold text-gray-900 mb-1">
                {{ media.title }}
              </h3>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div v-if="media.fileName">
                <span class="font-medium text-gray-700">{{ t("file_name") }}:</span>
                <span class="ml-2 text-gray-600">{{ media.fileName }}</span>
              </div>
              <div v-if="media.uploadedBy">
                <span class="font-medium text-gray-700">{{ t("uploaded_by") }}:</span>
                <span class="ml-2 text-gray-600">{{ media.uploadedBy }}</span>
              </div>
              <div v-if="media.createdAt">
                <span class="font-medium text-gray-700">{{ t("uploaded_on") }}:</span>
                <span class="ml-2 text-gray-600">{{ d(media.createdAt, "short") }}</span>
              </div>
              <div>
                <span class="font-medium text-gray-700">{{ t("media_table.headers.type") }}:</span>
                <span class="ml-2 text-gray-600">
                  {{ media.type === MediaType.Vid ? t("video") : t("image") }}
                </span>
              </div>
              <div>
                <span class="font-medium text-gray-700">{{ t("album") }}:</span>
                <span class="ml-2 text-gray-600">{{ media.album?.title ?? t("media_table.main_gallery") }}</span>
              </div>
            </div>
            <div v-if="media.description" class="pt-2">
              <span class="font-medium text-gray-700 block mb-1">{{ t("description") }}:</span>
              <p class="text-gray-600">
                {{ media.description }}
              </p>
            </div>
            <div
              v-if="isTaggingEnabled && media.tags && media.tags.length > 0"
              class="pt-2"
            >
              <span class="font-medium text-gray-700 block mb-2 text-sm">{{ t("media_details_modal.tags") }}:</span>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="(tag, index) in media.tags"
                  :key="index"
                  class="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                >
                  {{ tag }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
